import { useEffect, useState } from 'react';

import { getFirstToken } from '../helper/common';
import { PayWith, BOMPaymentToken, CardPaymentToken, PaymentTokenResponse, UseGetPaymentTokens } from '../type';

import PaymentService from 'services/payments/payment';

/**
 * A custom hook to fetch and manage payment tokens for card/bank muscat.
 * @returns {UseGetPaymentTokens} The payment tokens state.
 */
const useGetPaymentTokens = (): UseGetPaymentTokens => {
	const [refresh, setRefresh] = useState<number>();
	const [paymentTokens, setPaymentTokens] = useState<Omit<UseGetPaymentTokens, 'refresh'>>({
		loading: true,
		defaultMethod: PayWith.CARD,
		BOM: { tokens: [], defaultToken: undefined },
		cards: { tokens: [], defaultToken: undefined },
	});

	useEffect(() => {
		const subscription = PaymentService.getPaymentTokensV2().subscribe(
			({ response }) => {
				const { tokens, defaultMethod } = response?.data as PaymentTokenResponse;

				const defaultToken: { BOM?: BOMPaymentToken; card?: CardPaymentToken } = {};

				// Categorize payment tokens into BOM and card arrays while finding default tokens
				const categorizedTokens = tokens?.reduce(
					(acc, token) => {
						if (token.tokenType === PayWith.BM) {
							acc.BOM.push(token as BOMPaymentToken);

							// Set default token if not already set
							if (!defaultToken.BOM && token.default) {
								defaultToken.BOM = token as BOMPaymentToken;
							}
						} else {
							acc.card.push(token as CardPaymentToken);

							// Set default token if not already set
							if (!defaultToken.card && token.default) {
								defaultToken.card = token as CardPaymentToken;
							}
						}
						return acc;
					},
					{ BOM: [] as BOMPaymentToken[], card: [] as CardPaymentToken[] }
				);

				setPaymentTokens({
					loading: false,
					defaultMethod: defaultMethod ?? PayWith.CARD,
					BOM: {
						tokens: categorizedTokens?.BOM,
						defaultToken: defaultToken?.BOM ?? (getFirstToken(categorizedTokens.BOM) as BOMPaymentToken),
					},
					cards: {
						tokens: categorizedTokens?.card,
						defaultToken: defaultToken?.card ?? (getFirstToken(categorizedTokens.card) as CardPaymentToken),
					},
				});
			},
			() => setPaymentTokens((state) => ({ ...state, loading: false }))
		);

		return () => subscription.unsubscribe();
	}, [refresh]);

	return { ...paymentTokens, refresh: () => setRefresh(+new Date()) };
};

export default useGetPaymentTokens;
